import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  InputAdornment,
  Chip,
  Stack,
} from '@mui/material';
import { Search as SearchIcon, Warning as WarningIcon } from '@mui/icons-material';
import { tariffApi } from '../api';

interface AvailableTariff {
  id: string;
  title: string;
  vendor: string;
  isActive: boolean;
  enabled: boolean;
  aliasTariff: { id: string; title: string; vendor: string } | null;
}

interface NewTariffDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (tariffId: number) => void;
}

const DEFAULT_THANKS_PAGE = 'https://www.zahnzusatzversicherungen-vergleich.com/meine-anfrage/';

export function NewTariffDialog({ open, onClose, onCreated }: NewTariffDialogProps) {
  const [step, setStep] = useState<'select' | 'form'>('select');
  const [available, setAvailable] = useState<AvailableTariff[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AvailableTariff | null>(null);

  // Form fields
  const [tarif, setTarif] = useState('');
  const [versicherung, setVersicherung] = useState('');
  const [thanksPage, setThanksPage] = useState(DEFAULT_THANKS_PAGE);
  const [automationProvider, setAutomationProvider] = useState('DocuSign');
  const [templateIds, setTemplateIds] = useState({ SAME: '', DIFFERENT: '', DIFFERENT_EMAIL_KIDS: '', DIFFERENT_POST_KIDS: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      setStep('select');
      setSelected(null);
      setError(null);
      setSearch('');
      fetchAvailable();
    }
  }, [open]);

  const fetchAvailable = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tariffApi.getAvailableTariffs();
      setAvailable(data.tariffs);
    } catch (err) {
      setError('Failed to load available tariffs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (t: AvailableTariff) => {
    setSelected(t);
    setTarif(t.title);
    setVersicherung(t.vendor);
    setThanksPage(DEFAULT_THANKS_PAGE);
    setAutomationProvider('DocuSign');
    setTemplateIds({ SAME: '', DIFFERENT: '', DIFFERENT_EMAIL_KIDS: '', DIFFERENT_POST_KIDS: '' });
    setStep('form');
  };

  const handleCreate = async () => {
    if (!selected) return;
    try {
      setCreating(true);
      setError(null);
      await tariffApi.createTariff({
        id: Number(selected.id),
        TARIF: tarif,
        VERSICHERUNG: versicherung,
        thanksPage,
        automationProvider,
        automationProviderSettings: { templateId: templateIds },
      });
      onCreated(Number(selected.id));
      onClose();
    } catch (err) {
      setError('Failed to create tariff');
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const filtered = available.filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.id.includes(q) || t.title.toLowerCase().includes(q) || t.vendor.toLowerCase().includes(q);
  }).sort((a, b) => {
    const aPrimary = a.isActive && !a.aliasTariff ? 0 : 1;
    const bPrimary = b.isActive && !b.aliasTariff ? 0 : 1;
    return aPrimary - bPrimary;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {step === 'select' ? 'Select Tariff to Import' : `New Tariff — ${selected?.id}`}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 'select' && (
          <>
            <TextField
              fullWidth size="small" placeholder="Search by ID, name or vendor..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }}
              sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : filtered.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {search ? 'No matching tariffs' : 'All tariffs already imported'}
              </Typography>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto', mx: -2 }}>
                {filtered.map((t) => (
                  <ListItemButton key={t.id} onClick={() => handleSelect(t)} sx={{ borderRadius: 1.5, mx: 1, mb: 0.25 }}>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="body2" fontWeight={500}>{t.title}</Typography>
                          <Chip label={t.id} size="small" variant="outlined" sx={{ borderRadius: 1.5, height: 20, fontSize: '0.7rem' }} />
                          {!t.isActive && (
                            <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label="Inactive" size="small" color="warning" variant="outlined" sx={{ borderRadius: 1.5, height: 20, fontSize: '0.7rem' }} />
                          )}
                          {!t.enabled && (
                            <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label="Disabled" size="small" color="warning" variant="outlined" sx={{ borderRadius: 1.5, height: 20, fontSize: '0.7rem' }} />
                          )}
                          {t.aliasTariff && (
                            <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label={`Alias → ${t.aliasTariff.title}`} size="small" color="warning" variant="outlined" sx={{ borderRadius: 1.5, height: 20, fontSize: '0.7rem' }} />
                          )}
                        </Stack>
                      }
                      secondary={t.vendor}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        )}

        {step === 'form' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth size="small" label="TARIF" value={tarif} onChange={(e) => setTarif(e.target.value)} />
            <TextField fullWidth size="small" label="VERSICHERUNG" value={versicherung} onChange={(e) => setVersicherung(e.target.value)} />
            <TextField fullWidth size="small" label="Thanks Page URL" value={thanksPage} onChange={(e) => setThanksPage(e.target.value)} />

            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mt: 1 }}>
              DocuSign Template IDs
            </Typography>
            <TextField fullWidth size="small" label="SAME" value={templateIds.SAME} onChange={(e) => setTemplateIds((p) => ({ ...p, SAME: e.target.value }))} />
            <TextField fullWidth size="small" label="DIFFERENT" value={templateIds.DIFFERENT} onChange={(e) => setTemplateIds((p) => ({ ...p, DIFFERENT: e.target.value }))} />
            <TextField fullWidth size="small" label="DIFFERENT_EMAIL_KIDS" value={templateIds.DIFFERENT_EMAIL_KIDS} onChange={(e) => setTemplateIds((p) => ({ ...p, DIFFERENT_EMAIL_KIDS: e.target.value }))} />
            <TextField fullWidth size="small" label="DIFFERENT_POST_KIDS" value={templateIds.DIFFERENT_POST_KIDS} onChange={(e) => setTemplateIds((p) => ({ ...p, DIFFERENT_POST_KIDS: e.target.value }))} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {step === 'form' && (
          <Button onClick={() => setStep('select')} sx={{ textTransform: 'none', mr: 'auto' }}>
            ← Back
          </Button>
        )}
        <Button onClick={onClose} disabled={creating} sx={{ textTransform: 'none' }}>Cancel</Button>
        {step === 'form' && (
          <Button
            variant="contained" onClick={handleCreate} disabled={creating || !tarif || !versicherung}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {creating ? <CircularProgress size={20} /> : 'Create Tariff'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
